import React from 'react';
import { LuCircleAlert, LuRefreshCw } from 'react-icons/lu';

/**
 * NotificationList — presentational list body for the drawer.
 *
 * Renders four states off the `useUnreadNotifications` contract:
 *   - error   → inline error card with a retry action
 *   - loading → three skeleton rows (no spinner spam)
 *   - empty   → "You're all caught up" state (icon injected from
 *               parent so the drawer + any future page share it)
 *   - list    → notification items, grouped by unread first
 *
 * Item rendering is deliberately simple — the shared list-item
 * markup is co-located so we don't ship half a design system for
 * one surface. Extract to its own file when the shape stabilises.
 */
function relativeTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function NotificationItem({ item, onSelect }) {
    const isUnread = item.unread === true;
    const tone = item.tone || 'neutral';
    const clickable = !!item.href || typeof onSelect === 'function';

    const handleClick = () => { if (clickable) onSelect?.(item); };
    const handleKey = (e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect?.(item);
        }
    };

    return (
        <li className={`gc-notification-item${isUnread ? ' is-unread' : ''}`} data-tone={tone}>
            {/* Whole row is a single interactive element for pointer
                + keyboard users. Non-actionable items degrade to a
                static row (no button role, no focus ring). */}
            {clickable ? (
                <button
                    type="button"
                    className="gc-notification-item__button"
                    onClick={handleClick}
                    onKeyDown={handleKey}
                    aria-current={isUnread ? 'true' : undefined}
                >
                    <NotificationBody item={item} isUnread={isUnread} />
                </button>
            ) : (
                <div className="gc-notification-item__button" aria-disabled="true">
                    <NotificationBody item={item} isUnread={isUnread} />
                </div>
            )}
        </li>
    );
}

function NotificationBody({ item, isUnread }) {
    const Icon = item.icon || null;
    return (
        <>
            <span className="gc-notification-item__icon" aria-hidden="true">
                {Icon ? <Icon /> : null}
                {isUnread && <span className="gc-notification-item__dot" aria-hidden="true" />}
            </span>
            <span className="gc-notification-item__content">
                <span className="gc-notification-item__title">
                    {item.title}
                    {isUnread && (
                        <span className="gc-notification-item__unread-sr" aria-hidden="false">
                            {' '}(unread)
                        </span>
                    )}
                </span>
                {item.body && (
                    <span className="gc-notification-item__body">{item.body}</span>
                )}
                {item.timestamp && (
                    <span className="gc-notification-item__time">
                        {relativeTime(item.timestamp)}
                    </span>
                )}
            </span>
        </>
    );
}

function NotificationList({
    notifications,
    isLoading,
    error,
    onSelect,
    onRetry,
    emptyIcon,
}) {
    if (error) {
        return (
            <div className="gc-notification-state gc-notification-state--error" role="alert">
                <LuCircleAlert aria-hidden="true" className="gc-notification-state__icon" />
                <p className="gc-notification-state__title">Couldn't load notifications</p>
                <p className="gc-notification-state__desc">
                    Check your connection and try again.
                </p>
                {typeof onRetry === 'function' && (
                    <button
                        type="button"
                        className="gc-notification-state__retry"
                        onClick={onRetry}
                    >
                        <LuRefreshCw aria-hidden="true" />
                        <span>Try again</span>
                    </button>
                )}
            </div>
        );
    }

    if (isLoading) {
        return (
            <ul className="gc-notification-list" aria-busy="true" aria-live="polite">
                {[0, 1, 2].map((k) => (
                    <li key={k} className="gc-notification-item is-skeleton" aria-hidden="true">
                        <span className="gc-notification-item__icon" />
                        <span className="gc-notification-item__content">
                            <span className="gc-notification-item__title" />
                            <span className="gc-notification-item__body" />
                        </span>
                    </li>
                ))}
            </ul>
        );
    }

    const items = Array.isArray(notifications) ? notifications : [];

    if (items.length === 0) {
        return (
            <div className="gc-notification-state gc-notification-state--empty" role="status">
                <span className="gc-notification-state__icon" aria-hidden="true">
                    {emptyIcon}
                </span>
                <p className="gc-notification-state__title">You're all caught up</p>
                <p className="gc-notification-state__desc">
                    Charging and account updates will appear here.
                </p>
            </div>
        );
    }

    // Unread first, then most recent — same ordering the receipt
    // history uses, so users see one consistent chronology model
    // across the app.
    const sorted = [...items].sort((a, b) => {
        if (!!a.unread !== !!b.unread) return a.unread ? -1 : 1;
        const at = new Date(a.timestamp || 0).getTime();
        const bt = new Date(b.timestamp || 0).getTime();
        return bt - at;
    });

    return (
        <ul className="gc-notification-list">
            {sorted.map((item) => (
                <NotificationItem
                    key={item.id ?? item.title}
                    item={item}
                    onSelect={onSelect}
                />
            ))}
        </ul>
    );
}

export default NotificationList;
